// vCard generation utilities

export interface VCardPhoneNumber {
  id: string;
  type: 'mobile' | 'work' | 'home' | 'fax' | 'other';
  countryCode: string;
  number: string;
}

export interface VCardEmail {
  id: string;
  type: 'work' | 'personal' | 'other';
  email: string;
}

export interface VCardWebsite {
  id: string;
  type: 'website' | 'linkedin' | 'portfolio' | 'other';
  url: string;
  label?: string;
}

export interface VCardAddress {
  id: string;
  type: 'home' | 'work' | 'other';
  street1: string;
  street2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

export interface VCardSocialLinks {
  linkedin: string;
  twitter: string;
  instagram: string;
  facebook: string;
  github: string;
  custom: Array<{ label: string; url: string }>;
}

export interface VCardData {
  // Personal Info
  prefix: string;
  firstName: string;
  middleName: string;
  lastName: string;
  suffix: string;
  jobTitle: string;
  company: string;
  department: string;
  photoUrl: string;
  
  // Contact Details
  phoneNumbers: VCardPhoneNumber[];
  emails: VCardEmail[];
  websites: VCardWebsite[];
  
  // Addresses
  addresses: VCardAddress[];
  
  // Social Media
  social: VCardSocialLinks;
  
  // Additional Info
  birthday: string;
  anniversary: string;
  notes: string;
  organizationUrl: string;
  timeZone: string;
  
  // Options
  vcardVersion: '3.0' | '4.0';
  includePhoto: boolean;
  minimalVCard: boolean;
}

export const defaultVCardData: VCardData = {
  prefix: '',
  firstName: '',
  middleName: '',
  lastName: '',
  suffix: '',
  jobTitle: '',
  company: '',
  department: '',
  photoUrl: '',
  phoneNumbers: [{ id: '1', type: 'mobile', countryCode: 'US', number: '' }],
  emails: [{ id: '1', type: 'work', email: '' }],
  websites: [],
  addresses: [],
  social: {
    linkedin: '',
    twitter: '',
    instagram: '',
    facebook: '',
    github: '',
    custom: [],
  },
  birthday: '',
  anniversary: '',
  notes: '',
  organizationUrl: '',
  timeZone: '',
  vcardVersion: '3.0',
  includePhoto: false,
  minimalVCard: false,
};

// Phone type mapping for vCard
const phoneTypeMap: Record<string, string> = {
  mobile: 'CELL',
  work: 'WORK',
  home: 'HOME',
  fax: 'FAX',
  other: 'VOICE',
};

// Email type mapping
const emailTypeMap: Record<string, string> = {
  work: 'WORK',
  personal: 'HOME',
  other: 'INTERNET',
};

// Address type mapping
const addressTypeMap: Record<string, string> = {
  home: 'HOME',
  work: 'WORK',
  other: 'POSTAL',
};

// Escape special characters for vCard
function escapeVCard(str: string): string {
  if (!str) return '';
  return str
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
}

// Format date for vCard (YYYYMMDD or YYYY-MM-DD for v4)
function formatVCardDate(dateStr: string, version: '3.0' | '4.0'): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '';
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return version === '4.0' ? `${year}-${month}-${day}` : `${year}${month}${day}`;
}

export function generateVCard(data: VCardData): string {
  const lines: string[] = [];
  const v = data.vcardVersion;
  
  lines.push('BEGIN:VCARD');
  lines.push(`VERSION:${v}`);
  
  // Name (required)
  const fullName = [data.prefix, data.firstName, data.middleName, data.lastName, data.suffix]
    .filter(Boolean)
    .join(' ');
  
  if (v === '4.0') {
    lines.push(`N:${escapeVCard(data.lastName)};${escapeVCard(data.firstName)};${escapeVCard(data.middleName)};${escapeVCard(data.prefix)};${escapeVCard(data.suffix)}`);
    lines.push(`FN:${escapeVCard(fullName)}`);
  } else {
    lines.push(`N:${escapeVCard(data.lastName)};${escapeVCard(data.firstName)};${escapeVCard(data.middleName)};${escapeVCard(data.prefix)};${escapeVCard(data.suffix)}`);
    lines.push(`FN:${escapeVCard(fullName)}`);
  }
  
  // Minimal vCard - only essential info
  if (data.minimalVCard) {
    // Primary phone
    const primaryPhone = data.phoneNumbers.find(p => p.number);
    if (primaryPhone) {
      const dialCode = getDialCode(primaryPhone.countryCode);
      lines.push(`TEL;TYPE=${phoneTypeMap[primaryPhone.type]}:${dialCode}${primaryPhone.number.replace(/\D/g, '')}`);
    }
    
    // Primary email
    const primaryEmail = data.emails.find(e => e.email);
    if (primaryEmail) {
      lines.push(`EMAIL;TYPE=${emailTypeMap[primaryEmail.type]}:${primaryEmail.email}`);
    }
    
    lines.push('END:VCARD');
    return lines.join('\r\n');
  }
  
  // Organization
  if (data.company || data.department) {
    const org = data.department ? `${data.company};${data.department}` : data.company;
    lines.push(`ORG:${escapeVCard(org)}`);
  }
  
  // Title
  if (data.jobTitle) {
    lines.push(`TITLE:${escapeVCard(data.jobTitle)}`);
  }
  
  // Phone numbers
  data.phoneNumbers.forEach(phone => {
    if (phone.number) {
      const dialCode = getDialCode(phone.countryCode);
      const fullNumber = `${dialCode}${phone.number.replace(/\D/g, '')}`;
      if (v === '4.0') {
        lines.push(`TEL;TYPE=${phoneTypeMap[phone.type]};VALUE=uri:tel:${fullNumber}`);
      } else {
        lines.push(`TEL;TYPE=${phoneTypeMap[phone.type]}:${fullNumber}`);
      }
    }
  });
  
  // Emails
  data.emails.forEach(email => {
    if (email.email) {
      if (v === '4.0') {
        lines.push(`EMAIL;TYPE=${emailTypeMap[email.type]}:${email.email}`);
      } else {
        lines.push(`EMAIL;TYPE=INTERNET;TYPE=${emailTypeMap[email.type]}:${email.email}`);
      }
    }
  });
  
  // Websites
  data.websites.forEach(website => {
    if (website.url) {
      if (v === '4.0') {
        lines.push(`URL;TYPE=${website.type.toUpperCase()}:${website.url}`);
      } else {
        lines.push(`URL:${website.url}`);
      }
    }
  });
  
  // Organization URL
  if (data.organizationUrl) {
    lines.push(`URL;TYPE=WORK:${data.organizationUrl}`);
  }
  
  // Addresses
  data.addresses.forEach(addr => {
    if (addr.city || addr.street1) {
      const addrLine = `;;${escapeVCard(addr.street1)}${addr.street2 ? ' ' + escapeVCard(addr.street2) : ''};${escapeVCard(addr.city)};${escapeVCard(addr.state)};${escapeVCard(addr.postalCode)};${escapeVCard(addr.country)}`;
      if (v === '4.0') {
        lines.push(`ADR;TYPE=${addressTypeMap[addr.type]}:${addrLine}`);
      } else {
        lines.push(`ADR;TYPE=${addressTypeMap[addr.type]}:${addrLine}`);
      }
    }
  });
  
  // Birthday
  if (data.birthday) {
    const bday = formatVCardDate(data.birthday, v);
    if (bday) {
      lines.push(`BDAY:${bday}`);
    }
  }
  
  // Anniversary (v4 only)
  if (data.anniversary && v === '4.0') {
    const anniv = formatVCardDate(data.anniversary, v);
    if (anniv) {
      lines.push(`ANNIVERSARY:${anniv}`);
    }
  }
  
  // Social media (as URLs or X-properties)
  if (data.social.linkedin) {
    lines.push(`X-SOCIALPROFILE;TYPE=linkedin:${data.social.linkedin}`);
  }
  if (data.social.twitter) {
    const handle = data.social.twitter.startsWith('@') ? data.social.twitter.slice(1) : data.social.twitter;
    lines.push(`X-SOCIALPROFILE;TYPE=twitter:https://twitter.com/${handle}`);
  }
  if (data.social.instagram) {
    const handle = data.social.instagram.startsWith('@') ? data.social.instagram.slice(1) : data.social.instagram;
    lines.push(`X-SOCIALPROFILE;TYPE=instagram:https://instagram.com/${handle}`);
  }
  if (data.social.facebook) {
    lines.push(`X-SOCIALPROFILE;TYPE=facebook:${data.social.facebook}`);
  }
  if (data.social.github) {
    lines.push(`X-SOCIALPROFILE;TYPE=github:https://github.com/${data.social.github}`);
  }
  data.social.custom.forEach(custom => {
    if (custom.url) {
      lines.push(`X-SOCIALPROFILE;TYPE=${escapeVCard(custom.label)}:${custom.url}`);
    }
  });
  
  // Time zone
  if (data.timeZone) {
    lines.push(`TZ:${data.timeZone}`);
  }
  
  // Notes
  if (data.notes) {
    lines.push(`NOTE:${escapeVCard(data.notes)}`);
  }
  
  // Photo (as URL reference - embedding base64 makes QR too large)
  if (data.photoUrl && data.includePhoto) {
    if (v === '4.0') {
      lines.push(`PHOTO;MEDIATYPE=image/jpeg:${data.photoUrl}`);
    } else {
      lines.push(`PHOTO;VALUE=URI:${data.photoUrl}`);
    }
  }
  
  lines.push('END:VCARD');
  
  return lines.join('\r\n');
}

// Download vCard as .vcf file
export function downloadVCard(data: VCardData, filename?: string): void {
  const vcardContent = generateVCard(data);
  const blob = new Blob([vcardContent], { type: 'text/vcard;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || `${data.firstName}_${data.lastName}.vcf`.replace(/\s+/g, '_');
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Helper to get dial code from country code
function getDialCode(countryCode: string): string {
  const dialCodes: Record<string, string> = {
    'US': '+1', 'GB': '+44', 'IN': '+91', 'CA': '+1', 'AU': '+61',
    'DE': '+49', 'FR': '+33', 'IT': '+39', 'ES': '+34', 'BR': '+55',
    'MX': '+52', 'JP': '+81', 'CN': '+86', 'KR': '+82', 'RU': '+7',
    'ZA': '+27', 'AE': '+971', 'SA': '+966', 'SG': '+65', 'HK': '+852',
    'NZ': '+64', 'IE': '+353', 'NL': '+31', 'BE': '+32', 'CH': '+41',
    'AT': '+43', 'SE': '+46', 'NO': '+47', 'DK': '+45', 'FI': '+358',
    'PL': '+48', 'PT': '+351', 'GR': '+30', 'TR': '+90', 'IL': '+972',
  };
  return dialCodes[countryCode] || '+1';
}

// Convert VCardData to flat content data for storage
export function vcardToContentData(data: VCardData): Record<string, string> {
  return {
    vcardData: JSON.stringify(data),
    firstName: data.firstName,
    lastName: data.lastName,
    // Keep some fields at top level for backwards compatibility
    organization: data.company,
    title: data.jobTitle,
    email: data.emails[0]?.email || '',
    phone: data.phoneNumbers[0] ? `${getDialCode(data.phoneNumbers[0].countryCode)}${data.phoneNumbers[0].number}` : '',
  };
}

// Parse stored content data back to VCardData
export function contentDataToVCard(contentData: Record<string, string>): VCardData {
  if (contentData.vcardData) {
    try {
      return JSON.parse(contentData.vcardData) as VCardData;
    } catch {
      // Fall through to legacy parsing
    }
  }
  
  // Legacy format support
  return {
    ...defaultVCardData,
    firstName: contentData.firstName || '',
    lastName: contentData.lastName || '',
    company: contentData.organization || '',
    jobTitle: contentData.title || '',
    emails: contentData.email 
      ? [{ id: '1', type: 'work' as const, email: contentData.email }]
      : [{ id: '1', type: 'work' as const, email: '' }],
    phoneNumbers: contentData.phone
      ? [{ id: '1', type: 'mobile' as const, countryCode: 'US', number: contentData.phone.replace(/^\+\d+/, '') }]
      : [{ id: '1', type: 'mobile' as const, countryCode: 'US', number: '' }],
  };
}

// Validate required fields
export function validateVCard(data: VCardData): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  if (!data.firstName?.trim()) {
    errors.push('First name is required');
  }
  if (!data.lastName?.trim()) {
    errors.push('Last name is required');
  }
  
  // At least one contact method
  const hasPhone = data.phoneNumbers.some(p => p.number?.trim());
  const hasEmail = data.emails.some(e => e.email?.trim());
  
  if (!hasPhone && !hasEmail) {
    errors.push('At least one phone number or email is required');
  }
  
  // Validate email format
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  data.emails.forEach((e, i) => {
    if (e.email && !emailRegex.test(e.email)) {
      errors.push(`Email ${i + 1} is invalid`);
    }
  });
  
  // Validate URLs
  const urlRegex = /^https?:\/\/.+/;
  data.websites.forEach((w, i) => {
    if (w.url && !urlRegex.test(w.url)) {
      errors.push(`Website ${i + 1} must start with http:// or https://`);
    }
  });
  
  return {
    isValid: errors.length === 0,
    errors,
  };
}
